/**
 * YourPriceControl — "what would you actually buy?" One small control per trip.
 *
 * Most travellers want to avoid extras, so every extra starts OFF, and each
 * toggle carries its value judgement ("Skip it unless you need to sit
 * together") so the traveller sees what skipping costs. Saving re-prices every
 * flight: the one number on each row becomes their price, and it is the price
 * TripMuscle watches.
 *
 * `PriceChoiceForm` is purely presentational (props in, events out) so its
 * states are testable without a browser; `YourPriceControl` owns the draft, the
 * save and the plan refresh.
 */
import { useEffect, useId, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Minus, Plus, SlidersHorizontal } from "lucide-react";
import type { ExtraKind, PlanExtra, PlanPriceChoice } from "@/lib/fareOptionsTypes";
import type { PlanLeg } from "@/lib/types";
import { tripPlanApi } from "@/lib/api";
import {
  DEFAULT_PRICE_CHOICE,
  FARE_CHOICES,
  MAX_CHECKED_BAGS,
  bagsText,
  choosableExtras,
  extraPriceText,
  panelKeyAction,
  sameChoice,
  stepBags,
  submitPriceChoice,
  toggleExtra,
  type SaveStatus,
} from "./fareLadderView";

interface FormProps {
  draft: PlanPriceChoice;
  extras: PlanExtra[];
  status: SaveStatus;
  dirty: boolean;
  onFareLevel: (v: PlanPriceChoice["fareLevel"]) => void;
  onBags: (n: number) => void;
  onToggleExtra: (kind: ExtraKind) => void;
  onSave: () => void;
}

export function PriceChoiceForm({ draft, extras, status, dirty, onFareLevel, onBags, onToggleExtra, onSave }: FormProps) {
  const uid = useId();
  const saving = status === "saving";
  return (
    <form
      aria-label="Your price"
      className="space-y-3 text-xs"
      onSubmit={(e) => {
        e.preventDefault();
        if (!saving && dirty) onSave();
      }}
    >
      <p className="text-muted-foreground">
        Choose what you would actually buy. We price every flight that way and watch that price for you. This applies to the whole trip.
      </p>

      <fieldset className="grid gap-1.5 sm:grid-cols-2">
        <legend className="col-span-full mb-0.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Fare</legend>
        {FARE_CHOICES.map((c) => {
          const id = uid + "-fare-" + c.value;
          const on = draft.fareLevel === c.value;
          return (
            <label
              key={c.value}
              htmlFor={id}
              className={
                "flex min-h-[40px] cursor-pointer items-start gap-2 rounded-md border px-2.5 py-1.5 transition-colors focus-within:ring-2 focus-within:ring-[hsl(var(--ring))] motion-reduce:transition-none " +
                (on ? "border-[hsl(var(--primary))]/50 bg-accent" : "border-border hover:bg-muted/60")
              }
            >
              <input
                id={id}
                type="radio"
                name={uid + "-fare"}
                value={c.value}
                checked={on}
                onChange={() => onFareLevel(c.value)}
                className="mt-0.5 h-3.5 w-3.5 shrink-0 accent-[hsl(var(--primary))]"
              />
              <span>
                <span className="block font-medium text-foreground">{c.label}</span>
                <span className="block text-muted-foreground">{c.meaning}</span>
              </span>
            </label>
          );
        })}
      </fieldset>

      <div className="flex items-center justify-between gap-3">
        <span id={uid + "-bags"} className="font-medium text-foreground">Checked bags</span>
        <span className="inline-flex items-center gap-2" role="group" aria-labelledby={uid + "-bags"}>
          <button
            type="button"
            aria-label="Fewer checked bags"
            disabled={draft.checkedBags <= 0}
            onClick={() => onBags(stepBags(draft.checkedBags, -1))}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border text-foreground transition-colors hover:bg-muted disabled:opacity-40 motion-reduce:transition-none"
          >
            <Minus className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
          <span aria-live="polite" className="w-14 text-center font-mono tabular-nums text-foreground">
            {bagsText(draft.checkedBags)}
          </span>
          <button
            type="button"
            aria-label="More checked bags"
            disabled={draft.checkedBags >= MAX_CHECKED_BAGS}
            onClick={() => onBags(stepBags(draft.checkedBags, 1))}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border text-foreground transition-colors hover:bg-muted disabled:opacity-40 motion-reduce:transition-none"
          >
            <Plus className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </span>
      </div>

      {extras.length > 0 && (
        <fieldset className="space-y-1.5">
          <legend className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Extras</legend>
          {extras.map((e) => {
            const on = draft.extras.includes(e.kind);
            const labelId = uid + "-x-" + e.kind;
            return (
              <div key={e.kind} className="flex items-start justify-between gap-3 rounded-md border border-border px-2.5 py-1.5">
                <div className="min-w-0">
                  <p id={labelId} className="font-medium text-foreground">{e.name}</p>
                  <p className={e.price == null ? "text-muted-foreground" : "tabular-nums text-foreground"}>
                    {extraPriceText(e)}
                  </p>
                  {(e.advice || e.details) && <p className="text-muted-foreground">{e.advice ?? e.details}</p>}
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={on}
                  aria-labelledby={labelId}
                  onClick={() => onToggleExtra(e.kind)}
                  className={
                    "relative mt-0.5 inline-flex h-6 w-10 shrink-0 items-center rounded-full border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] motion-reduce:transition-none " +
                    (on ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]" : "border-border bg-muted")
                  }
                >
                  <span
                    aria-hidden="true"
                    className={
                      "inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform motion-reduce:transition-none " +
                      (on ? "translate-x-[1.125rem]" : "translate-x-0.5")
                    }
                  />
                </button>
              </div>
            );
          })}
        </fieldset>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="submit"
          disabled={!dirty}
          aria-disabled={saving || !dirty}
          className="inline-flex min-h-[36px] items-center rounded-md bg-[hsl(var(--primary))] px-3 text-xs font-medium text-[hsl(var(--primary-foreground))] transition-opacity hover:opacity-90 disabled:opacity-50 motion-reduce:transition-none"
        >
          {saving ? "Saving…" : "Save and re-price"}
        </button>
        <span aria-live="polite" className="text-muted-foreground">
          {status === "saved" && !dirty && "Saved. Every flight is priced this way now."}
        </span>
        {status === "error" && (
          <span role="alert" className="font-medium text-destructive">
            We could not save that. Your choices are still here, so try again.
          </span>
        )}
      </div>
    </form>
  );
}

interface ControlProps {
  tripId: string;
  leg: Pick<PlanLeg, "priceChoice" | "priceChoiceExplicit" | "focus" | "matrix">;
  /** Open on first render (tests, screenshots). */
  defaultOpen?: boolean;
}

/** True when the page was opened from the trip email's "Set your price" link. */
export function wantsSetPrice(): boolean {
  return typeof window !== "undefined" && new URLSearchParams(window.location.search).get("setPrice") === "1";
}

export default function YourPriceControl({ tripId, leg, defaultOpen = false }: ControlProps) {
  const qc = useQueryClient();
  const saved = leg.priceChoice ?? DEFAULT_PRICE_CHOICE;
  const extras = choosableExtras(leg);
  // ?setPrice=1 is the door from the trip email's "Set your price" link.
  const fromEmail = useRef(wantsSetPrice());
  const [open, setOpen] = useState(defaultOpen || fromEmail.current);
  const [draft, setDraft] = useState<PlanPriceChoice>(saved);
  const [status, setStatus] = useState<SaveStatus>("idle");
  const panelId = useId();
  const trigger = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (fromEmail.current) trigger.current?.scrollIntoView({ block: "center" });
  }, []);

  // Escape closes the panel from anywhere, and puts focus back on the button
  // that opened it. A document listener, because the focused control (the save
  // button) can disable itself and drop focus out of the panel.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (panelKeyAction(e.key) === "close") {
        setOpen(false);
        trigger.current?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  // A fresh plan carries the saved choice; adopt it unless a save just failed
  // (the failed draft is exactly what the traveller wants to retry).
  const savedKey = JSON.stringify(saved);
  useEffect(() => {
    setDraft(saved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedKey]);

  const dirty = !sameChoice(draft, saved);
  const edit = (next: PlanPriceChoice) => {
    setDraft(next);
    if (status !== "saving") setStatus("idle");
  };

  const save = async () => {
    setStatus("saving");
    const result = await submitPriceChoice(
      (c) => tripPlanApi.setPriceChoice(tripId, c),
      () => qc.invalidateQueries({ queryKey: ["tripPlan", tripId] }),
      draft,
    );
    setStatus(result);
  };

  // With no saved choice the rows show the best fare, so the button says that.
  const best = !leg.priceChoiceExplicit && leg.focus?.bestFare ? leg.focus.bestFare : null;
  const summary = best ? best.fareLevel : FARE_CHOICES.find((c) => c.value === saved.fareLevel)?.label ?? "Cheapest";

  const backToBest = async () => {
    setStatus("saving");
    try {
      await tripPlanApi.clearPriceChoice(tripId);
      await qc.invalidateQueries({ queryKey: ["tripPlan", tripId] });
      setStatus("idle");
      setOpen(false);
    } catch {
      setStatus("error");
    }
  };

  // Two siblings, not one wrapper: the button sits in the options header row
  // and the panel takes its own full-width row below it.
  return (
    <>
      <button
        ref={trigger}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={panelId}
        className="inline-flex min-h-[32px] items-center gap-1.5 rounded-md border border-border px-2.5 text-xs font-medium text-foreground transition-colors hover:bg-muted motion-reduce:transition-none print:hidden"
      >
        <SlidersHorizontal className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
        Your price
        <span className="font-normal text-muted-foreground">{summary}</span>
      </button>
      {open && (
        <div id={panelId} className="order-last basis-full rounded-lg border bg-card px-3 py-3 shadow-paper print:hidden">
          <PriceChoiceForm
            draft={draft}
            extras={extras}
            status={status}
            dirty={dirty}
            onFareLevel={(v) => edit({ ...draft, fareLevel: v })}
            onBags={(n) => edit({ ...draft, checkedBags: n })}
            onToggleExtra={(k) => edit({ ...draft, extras: toggleExtra(draft.extras, k) })}
            onSave={save}
          />
          {leg.priceChoiceExplicit && (
            <button
              type="button"
              onClick={backToBest}
              disabled={status === "saving"}
              className="mt-2 text-xs font-medium text-[hsl(var(--primary))] underline-offset-2 hover:underline disabled:opacity-50"
            >
              Go back to my best fare
            </button>
          )}
        </div>
      )}
    </>
  );
}
